import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type RefObject, useCallback, useRef, useState,
} from 'react';
import {
  getScrollableElementFromScrollbarRef, isScrollXPossible, isScrollYPossible,
} from '~/_shared/components/scrollbar/utils';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';

const SCROLL_STEP = 3;
const SCROLL_TOLERANCE_PX = 1;

export function useScrollControls(scrollbarRef: RefObject<OverlayScrollbarsComponentRef | null>, scrollStep = SCROLL_STEP) {
  const scrollAnimationFrameId = useRef<number | null>(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const [scrollXPossible, setScrollXPossible] = useState(false);
  const [scrollYPossible, setScrollYPossible] = useState(false);

  const onUpdate = useThrottle(() => {
    const scrollbarElement = getScrollableElementFromScrollbarRef(scrollbarRef.current);
    if (scrollbarElement) {
      setAtTop(isScrollbarAtTop(scrollbarElement));
      setAtBottom(isScrollbarAtBottom(scrollbarElement));
      setScrollXPossible(isScrollXPossible(scrollbarElement));
      setScrollYPossible(isScrollYPossible(scrollbarElement));
    }
  }, [scrollbarRef], 50);

  const continuousScroll = useCallback((direction: 'up' | 'down') => {
    const scrollbarElement = getScrollableElementFromScrollbarRef(scrollbarRef.current);
    if (!scrollbarElement) {
      return;
    }

    const currentScrollTop = scrollbarElement.scrollTop;
    const newScrollTop = direction === 'up'
      ? Math.max(0, currentScrollTop - scrollStep)
      : currentScrollTop + scrollStep;

    if ((direction === 'up') && isScrollbarAtTop(scrollbarElement)) {
      return;
    }

    if ((direction === 'down') && isScrollbarAtBottom(scrollbarElement)) {
      return;
    }

    scrollbarElement.scrollTo({ top: newScrollTop, behavior: 'smooth' });
    scrollAnimationFrameId.current = requestAnimationFrame(() => continuousScroll(direction));
  }, [scrollStep, scrollbarRef]);

  const stopScrolling = useCallback(() => {
    if (scrollAnimationFrameId.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameId.current);
      scrollAnimationFrameId.current = null;
    }
  }, []);

  const scrollUp = useCallback(() => continuousScroll('up'), [continuousScroll]);
  const scrollDown = useCallback(() => continuousScroll('down'), [continuousScroll]);

  return {
    atBottom,
    atTop,
    scrollDown,
    scrollUp,
    scrollXPossible,
    scrollYPossible,
    stopScrolling,
    updateScrollBoundaries: onUpdate,
  };
}

const isScrollbarAtTop = (scrollState: { readonly scrollTop: number }) =>
  scrollState.scrollTop <= SCROLL_TOLERANCE_PX;

const isScrollbarAtBottom = (scrollState: {
  readonly scrollTop: number;
  readonly clientHeight: number;
  readonly scrollHeight: number;
}) => {
  return scrollState.scrollTop + scrollState.clientHeight + SCROLL_TOLERANCE_PX >= scrollState.scrollHeight;
};
